/**
 * The bridge between the design editor and chat (ADR 0015 §Desktop), kept outside editor
 * internals: the editor page registers what is open (design, current slide, selection) with one
 * hook call; the chat dock reads it to share "สไลด์ 4 · กล่อง 'ราคา'" as a card, to open
 * "แชทของงานนี้", and to tag design-chat messages with the sender's slide. Deep links
 * (#/d/<id>?page=<pageId>&node=<nodeId>&chat=<conversationId>) are applied here too.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { parseHash } from '../lib/router.js';

export interface EditorLike {
  getSnapshot(): { scene: { pages: { id: string; name?: string; children: unknown[] }[] }; pageId: string; selection: readonly string[] };
  subscribe(fn: () => void): () => void;
  setPage(pageId: string): void;
  setSelection(ids: readonly string[]): void;
}

export interface WorkContext {
  designId: string;
  title: string;
  kind: string;
  editor: EditorLike;
}

let current: WorkContext | null = null;
let v = 0;
const listeners = new Set<() => void>();
const bump = () => {
  v++;
  for (const fn of listeners) fn();
};

export function workContext(): WorkContext | null {
  return current;
}

export function useWorkContext(): WorkContext | null {
  useSyncExternalStore((fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, () => v, () => v);
  return current;
}

/** Requests to open the chat dock ("แชทของงานนี้", a pushed design-chat link). */
type DockRequest = { conversationId?: string; designId?: string };
const dockListeners = new Set<(r: DockRequest) => void>();
export function onDockRequest(fn: (r: DockRequest) => void): () => void {
  dockListeners.add(fn);
  return () => dockListeners.delete(fn);
}
export function openChatDock(r: DockRequest = {}): void {
  for (const fn of dockListeners) fn(r);
}

interface NodeLike {
  id: string;
  name?: string;
  type?: string;
  children?: NodeLike[];
}

function findIn(nodes: NodeLike[], id: string): NodeLike | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = n.children ? findIn(n.children, id) : null;
    if (hit) return hit;
  }
  return null;
}

/** What is selected right now, labelled like the server labels cards. */
export function currentSelection(ctx: WorkContext): { pageId: string; pageIndex: number; nodeId: string | null; label: string } {
  const snap = ctx.editor.getSnapshot();
  const pageIndex = Math.max(0, snap.scene.pages.findIndex((p) => p.id === snap.pageId));
  const page = snap.scene.pages[pageIndex]!;
  const nodeId = snap.selection[0] ?? null;
  const node = nodeId ? findIn(page.children as NodeLike[], nodeId) : null;
  const word = ctx.kind === 'presentation' ? 'สไลด์' : 'หน้า';
  const label = `${word} ${pageIndex + 1}${node ? ` · ${node.type === 'text' ? 'กล่อง' : 'วัตถุ'} '${node.name || (node.type === 'text' ? 'ข้อความ' : 'วัตถุ')}'` : ''}`;
  return { pageId: page.id, pageIndex, nodeId: node ? node.id : null, label };
}

/** Called once by the editor page (EditorPage.tsx). */
export function useChatWorkContext(design: { id: string; title: string; kind: string }, editor: EditorLike): void {
  useEffect(() => {
    current = { designId: design.id, title: design.title, kind: design.kind, editor };
    bump();
    const apply = () => {
      const { path, query } = parseHash(window.location.hash);
      if (path !== `/d/${design.id}`) return;
      const page = query.get('page');
      const node = query.get('node');
      if (page) editor.setPage(page);
      if (node) setTimeout(() => editor.setSelection([node]), 0);
      const chat = query.get('chat');
      if (chat) openChatDock({ conversationId: chat });
    };
    // The scene arrives with the page; apply the deep link once it is there.
    const t = setTimeout(apply, 50);
    window.addEventListener('hashchange', apply);
    return () => {
      clearTimeout(t);
      window.removeEventListener('hashchange', apply);
      if (current?.designId === design.id) {
        current = null;
        bump();
      }
    };
  }, [design.id, design.title, design.kind, editor]);
}
